import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtRelative } from "../lib/status";
import { IncidentStatusBadge, SeverityBadge, INCIDENT_TYPE_META } from "../lib/incidents";
import { Plus, Search, AlertOctagon, X } from "lucide-react";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "affecting_bookings", label: "Affecting Bookings" },
  { value: "resolved", label: "Resolved" },
  { value: "cancelled", label: "False Alarm" },
];

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const vehicleIdFilter = searchParams.get("vehicle_id");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.status = filter;
    if (vehicleIdFilter) params.vehicle_id = vehicleIdFilter;
    api.get("/incidents", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [filter, vehicleIdFilter]);

  const filteredVehicleName = useMemo(
    () => (vehicleIdFilter && items.find((i) => i.item_id === vehicleIdFilter)?.vehicle?.name) || null,
    [vehicleIdFilter, items],
  );

  const clearVehicleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("vehicle_id");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((i) =>
      i.title?.toLowerCase().includes(s)
      || i.vehicle?.name?.toLowerCase().includes(s)
      || i.incident_type?.toLowerCase().includes(s)
      || i.severity?.toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Problems</h1>
          <p className="text-slate-600 mt-1">Damaged vehicles, late returns, and other issues affecting bookings.</p>
        </div>
        <Link to={vehicleIdFilter ? `/app/incidents/new?vehicle_id=${vehicleIdFilter}` : "/app/incidents/new"} data-testid="incidents-new-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium">
          <Plus className="w-4 h-4" /> Log incident
        </Link>
      </div>

      {vehicleIdFilter && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900" data-testid="incidents-vehicle-filter">
          Showing incidents for{" "}
          <span className="font-medium">
            {filteredVehicleName || `vehicle ${vehicleIdFilter.slice(0, 8)}`}
          </span>
          <button
            type="button"
            onClick={clearVehicleFilter}
            data-testid="incidents-clear-vehicle-filter"
            className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-blue-100"
          >
            <X className="w-3 h-3" /> Clear
          </button>
          <Link
            to={`/app/vehicles/${vehicleIdFilter}/damages`}
            className="ml-2 text-xs underline hover:no-underline"
          >
            Back to vehicle
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="incidents-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, vehicle, type…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm w-72 max-w-full"
          />
        </div>
        <div className="flex flex-wrap gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.value} data-testid={`inc-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === f.value ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center" data-testid="incidents-empty">
            <AlertOctagon className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-2 text-slate-700 font-medium">No active vehicle problems</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              If a vehicle is damaged, late, or unavailable, log it here so affected customers can be warned.
            </p>
            <Link to="/app/incidents/new" data-testid="incidents-empty-cta"
                   className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-lg btn-brand text-sm font-medium">
              + Log Problem
            </Link>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {filtered.map((i) => (
            <li key={i.id} className="p-4 hover:bg-slate-50" data-testid={`incident-row-${i.id}`}>
              <Link to={`/app/incidents/${i.id}`} className="block">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{i.title}</span>
                      <SeverityBadge severity={i.severity} />
                      <IncidentStatusBadge status={i.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {INCIDENT_TYPE_META[i.incident_type] || i.incident_type} · {i.vehicle?.name || "-"}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>{fmtDate(i.start_datetime)}</div>
                    <div>{i.affected_count} affected booking{i.affected_count === 1 ? "" : "s"}</div>
                    <div>{fmtRelative(i.created_at)}</div>
                  </div>
                </div>
                {i.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{i.description}</p>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
